import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Nav from '../components/Nav'
import SceneSidebar from '../components/SceneSidebar'
import PhoneFrame from '../components/PhoneFrame'
import PreviewScreen from '../components/PreviewScreen'
import Playback from '../components/Playback'
import EditorPanel from '../components/EditorPanel'
import StudioTopbar from '../components/StudioTopbar'
import ExportModal from '../components/ExportModal'
import { api, ApiError } from '../api/client'
import { useEditorStore, clearHistory, undo, redo } from '../store/editor'
import type { SceneType } from '../api/types'

export default function Studio() {
  const { sceneId, projectId } = useParams()
  const nav = useNavigate()
  const { t } = useTranslation()
  const loadScene = useEditorStore((s) => s.loadScene)
  const setCurrentProject = useEditorStore((s) => s.setCurrentProject)
  const currentProjectId = useEditorStore((s) => s.currentProjectId)
  const currentProjectTitle = useEditorStore((s) => s.currentProjectTitle)
  const storeSceneId = useEditorStore((s) => s.sceneId)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showExport, setShowExport] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')

  // 加载：projectId 存在 → 从 DB 载入项目（scene_type 从 project 读）；否则载场景默认
  useEffect(() => {
    let alive = true
    setLoading(true)
    if (projectId) {
      // 已有项目：从 DB 载入 project.data + project.scene_type
      api.getProject(projectId)
        .then((pr) => {
          if (!alive) return
          const sid = pr.project.scene_type
          api.getScene(sid as SceneType).then((sr) => {
            if (!alive) return
            loadScene(sid as SceneType, pr.project.data, sr.meta.duration || 30)
            setCurrentProject(pr.project.id, pr.project.title)
            clearHistory()
            setError(null)
          })
        })
        .catch((e: unknown) => {
          if (alive) setError(e instanceof ApiError ? e.message : 'Project not found')
        })
        .finally(() => { if (alive) setLoading(false) })
    } else if (sceneId) {
      // 新项目：载入场景默认数据
      api.getScene(sceneId as SceneType)
        .then((sr) => {
          if (!alive) return
          loadScene(sceneId as SceneType, sr.default_data, sr.meta.duration || 30)
          setCurrentProject(null, '')
          clearHistory()
          setError(null)
        })
        .catch((e: unknown) => {
          if (alive) setError(e instanceof ApiError ? e.message : 'Failed to load')
        })
        .finally(() => { if (alive) setLoading(false) })
    }
    return () => {
      alive = false
    }
  }, [sceneId, projectId, loadScene, setCurrentProject])

  // 键盘：Ctrl+Z 撤销 / Ctrl+Shift+Z 或 Ctrl+Y 重做（全局，覆盖浏览器默认）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase()
      if ((e.ctrlKey || e.metaKey) && k === 'z') {
        e.preventDefault()
        if (e.shiftKey) redo()
        else undo()
      } else if ((e.ctrlKey || e.metaKey) && k === 'y') {
        e.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // 导出弹窗（L4）
  const onExport = () => setShowExport(true)

  // 保存项目：未登录则按钮不显示；新建 → prompt 标题 → create + update + URL 跳 /p/:id；已存 → update
  const onSave = async () => {
    const sid = useEditorStore.getState().sceneId
    if (!sid) return
    setSaveState('saving')
    try {
      const data = useEditorStore.getState().data
      if (!currentProjectId) {
        const title = window.prompt(t('studio.saveTitlePrompt'), currentProjectTitle || sid) || sid
        const { project } = await api.createProject({ scene_type: sid as SceneType, title })
        await api.updateProject(project.id, { title, data })
        setCurrentProject(project.id, title)
        nav(`/p/${project.id}`, { replace: true })  // URL 从 /s/:scene 跳 /p/:id
      } else {
        await api.updateProject(currentProjectId, { title: currentProjectTitle || sid, data })
      }
      setSaveState('saved')
      setTimeout(() => setSaveState('idle'), 1800)
    } catch (e: unknown) {
      setSaveState('idle')
      window.alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : t('studio.saveFail'))
    }
  }

  return (
    <div className="app-shell">
      <Nav />
      <StudioTopbar sceneName={storeSceneId ?? sceneId} onExport={onExport} onSave={onSave} saveState={saveState} />
      <div className="app-body studio-body">
        {error ? (
          <div className="studio-error">
            <div className="error-box">{error}</div>
            <button className="btn btn-outline btn-sm" onClick={() => nav('/')}>
              ← {t('common.back')}
            </button>
          </div>
        ) : (
          <div className="workbench">
            <SceneSidebar />
            <section className="preview-panel">
              <div className="panel-label">
                {t('studio.preview')} <span className="panel-sub">{t('studio.resolution')}</span>
              </div>
              <div className="preview-stage">
                {loading ? <span className="spinner" /> : <PhoneFrame><PreviewScreen /></PhoneFrame>}
              </div>
              <Playback />
              <button
                type="button"
                className="btn btn-primary editor-toggle"
                onClick={() => setEditorOpen(true)}
              >
                ✎ {t('studio.editor')}
              </button>
            </section>
            <EditorPanel open={editorOpen} onClose={() => setEditorOpen(false)} />
          </div>
        )}
        {showExport && <ExportModal onClose={() => setShowExport(false)} />}
      </div>
    </div>
  )
}
